import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import {
  Badge,
  CatalogWidget,
  FormSelect,
  StatusMessage,
  hasCatalogWidget,
  WidgetCardShell,
  WidgetGrid,
  WidgetGridProvider,
  WidgetSkeleton,
} from '@aimsos/ui';

import { PREVIEW_GRID_CONFIG, PREVIEW_WIDGET_ID } from '../constants/widgetPreviewLayout.js';

import { ImageOff } from 'lucide-react';

/** Time ranges the end-user filter row offers. */
const TIME_RANGES = Object.freeze(['last7', 'last30', 'last90', 'quarterToDate', 'last12Months']);

const NOOP = () => {};

/**
 * The rendered widget in the live preview (CT-5199).
 *
 * ## A canvas, not a card
 *
 * The preview is a one-widget `WidgetGrid` in edit mode — the same component
 * every studio overview is built on — so the widget behaves here exactly as it
 * will on a dashboard: grab the handle to move it, drag an edge to resize it.
 * An author sizing a widget is making a layout decision, and a static card
 * asks them to make it blind.
 *
 * It also retires a small lie. The size captions have always said "1/3 width",
 * "2/3 width", "Full width" while the card was actually sized with a
 * `max-width` picked to look about right. On a twelve-column grid those
 * fractions are the layout: four, eight and twelve columns.
 *
 * ## Sizing
 *
 * The canvas declares its own twelve columns rather than deriving them from
 * its ~530px of width, which would collapse to four and turn every size into
 * "full width". See `widgetPreviewLayout.js`.
 *
 * ## Data
 *
 * Draws through `CatalogWidget` — the builder owns no per-type renderers, and
 * neither does this app. Every widget type resolves through one registry in
 * `@aimsos/ui`, so the shape the author previews is the same object the
 * dashboard will render.
 *
 * `data` is the saved widget's executed result, or `null` — before the first
 * save, while the form is dirty, or for a type with no honest reading of a
 * result. `CatalogWidget` falls back to its sample for all three, which is the
 * right answer in every one of them.
 *
 * `isLoadingData` is the fourth case and the one the sample must not cover:
 * while the execution is in flight, drawing plausible numbers and replacing
 * them a moment later is worse than waiting, because nothing says the first
 * set was never real. The body is a skeleton until the result lands.
 *
 * `memo()` and a `previewWidget` object that excludes the name: the title is
 * its own prop, so typing a name re-renders the header and leaves the body
 * alone.
 */
const LivePreviewCard = memo(function LivePreviewCard({
  title,
  previewWidget,
  filterChips,
  size,
  layouts,
  onLayoutChange,
  timeRange,
  onTimeRangeChange,
  data,
  hasEmptyResult = false,
  isLoadingData,
}) {
  const { t } = useTranslation('dashboards');

  const { type, endUserFiltersEnabled } = previewWidget;

  // The builder previews a widget that has no query behind it yet, so the
  // catalogue's own sample fixture is the data — that is what `CatalogWidget`
  // falls back to. `hasCatalogWidget` is what separates "nothing to draw yet"
  // from "this type has no renderer", and only the second is worth an error.
  const canRender = hasCatalogWidget(type);

  const timeRangeOptions = useMemo(
    () =>
      TIME_RANGES.map((range) => ({
        value: range,
        label: t(`widgetBuilder.preview.timeRanges.${range}`),
      })),
    [t],
  );

  const widgetConfig = useMemo(
    () => ({
      widgets: [
        {
          instance_uuid: PREVIEW_WIDGET_ID,
          accepts_global_filters: false,
          layout: layouts.lg?.[0],
          config: { card: { title, header_buttons: [] } },
        },
      ],
      layouts,
      gridConfig: PREVIEW_GRID_CONFIG,
    }),
    [layouts, title],
  );

  // Three outcomes, picked before the JSX so the body is not a nested ternary:
  // waiting on the execution, drawing the widget, or saying this type has no
  // renderer — which an empty card would misreport as a broken widget.
  let body;
  if (isLoadingData) {
    body = <WidgetSkeleton className="min-h-[120px] flex-1" />;
  } else if (canRender) {
    body = (
      <CatalogWidget
        type={type}
        data={data ?? undefined}
        // The sample is for a widget that has not run. One that ran and
        // found nothing — a SUM over null rows — says so instead.
        useSampleData={!hasEmptyResult}
        compact={size === 'small'}
      />
    );
  } else {
    body = (
      <StatusMessage
        icon={ImageOff}
        title={t('widgetLibrary.detail.previewUnavailableTitle')}
        subtitle={t('widgetLibrary.detail.previewUnavailableSubtitle')}
      />
    );
  }

  const renderWidget = useCallback(
    (widget) => (
      <WidgetCardShell title={widget.config?.card?.title} isPinned={false}>
        {/* `h-full`: the charts flex their plot to the slot's height, and a
            flex child can only do that if its ancestors have one to give. */}
        <div className="flex h-full min-w-0 flex-col gap-3">
          {endUserFiltersEnabled ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2 border-b border-edge pb-3">
              <div className="min-w-[150px]">
                {/* Portaled: the widget card's body scrolls, so an absolutely
                    positioned list is clipped at the card's edge. No z-index
                    reaches out of a scroll container. */}
                <FormSelect
                  value={timeRange}
                  onChange={onTimeRangeChange}
                  options={timeRangeOptions}
                  portalListbox
                />
              </div>
              {filterChips.map((chip) => (
                <Badge key={chip.id} size="small" variant="secondary">
                  {`${chip.label} ${t(`widgetBuilder.filters.operators.${chip.operator}`)} ${chip.value ?? ''}`.trim()}
                </Badge>
              ))}
            </div>
          ) : null}

          {body}
        </div>
      </WidgetCardShell>
    ),
    [
      body,
      endUserFiltersEnabled,
      filterChips,
      onTimeRangeChange,
      t,
      timeRange,
      timeRangeOptions,
    ],
  );

  return (
    // `pl-3` leaves room for the drag handle, which `WidgetCardShell` floats
    // 12px outside the card's left edge. Without it the handle is clipped by
    // the preview column and the widget looks immovable.
    <div className="min-w-0 pl-3">
      <WidgetGridProvider isEditMode>
        <WidgetGrid
          // The grid's 200px floor keeps an empty dashboard from collapsing;
          // here it strands a compact widget 30px above its own caption.
          className="!min-h-0"
          externalWidgetConfig={widgetConfig}
          onExternalLayoutChange={onLayoutChange}
          onDashboardRetry={NOOP}
          renderWidget={renderWidget}
        />
      </WidgetGridProvider>
    </div>
  );
});

LivePreviewCard.propTypes = {
  title: PropTypes.string.isRequired,
  previewWidget: PropTypes.object.isRequired,
  filterChips: PropTypes.arrayOf(PropTypes.object).isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']).isRequired,
  /** react-grid-layout `layouts`, from `useWidgetPreviewLayout`. */
  layouts: PropTypes.object.isRequired,
  onLayoutChange: PropTypes.func.isRequired,
  timeRange: PropTypes.string.isRequired,
  onTimeRangeChange: PropTypes.func.isRequired,
  /** Executed result for the saved widget; `null` falls back to the sample. */
  data: PropTypes.object,
  /** The widget ran and the query answered with no value. */
  hasEmptyResult: PropTypes.bool,
  /** True while `widget/execute` is in flight, so the body waits. */
  isLoadingData: PropTypes.bool,
};

LivePreviewCard.defaultProps = {
  data: null,
  hasEmptyResult: false,
  isLoadingData: false,
};

export default LivePreviewCard;
